import { useMemo } from 'react';
import styled from 'styled-components';

import { SecondaryButton, BUTTON_SIZES } from 'ui/button';
import { Tooltip } from 'ui/tooltip';
import { Export24 } from 'ui/icons';
import { useDispatch } from 'react-redux';
import { downloadAsCsvFile } from 'utils/csv';
import { addPositiveToast, addNegativeToast } from 'modules/toastContainer';
import { FeatureFlag } from 'modules/featureFlags/types';

const StyledButton = styled(SecondaryButton)`
  color: ${({ theme }) => theme.colors.textColors.textPrimary};
  border: ${({ theme }) => theme.spacing.single} #bdbdbd solid;
  margin-left: 5px;
`;

type CsvExportButtonProps = {
  sortedFeatures: FeatureFlag[];
};

const TOOLTIP_DISPLAY_TEXT = {
  disabledTooltip: 'Export will be available once the table has feature flags',
};

export const CSV_EXPORT_HEADERS = [
  {
    label: 'Feature',
    key: 'feature',
  },
  {
    label: 'Description',
    key: 'notes',
  },
  {
    label: 'Fractional',
    key: 'isFractional',
  },
  {
    label: 'Enabled Globally',
    key: 'enabledGlobally',
  },
  {
    label: 'Enabled Orgs',
    key: 'enabledOrgIds',
  },
  {
    label: 'Total Enabled Orgs',
    key: 'enabledOrgIdsTotal',
  },
  {
    label: 'Enabled Projects',
    key: 'enabledProjectIds',
  },
  {
    label: 'Total Enabled Projects',
    key: 'enabledProjectIdsTotal',
  },
  {
    label: 'Disabled Orgs',
    key: 'disabledOrgIds',
  },
  {
    label: 'Total Disabled Orgs',
    key: 'disabledOrgIdsTotal',
  },
  {
    label: 'Disabled Projects',
    key: 'disabledProjectIds',
  },
  {
    label: 'Total Disabled Projects',
    key: 'disabledProjectIdsTotal',
  },
];

const headerRow = CSV_EXPORT_HEADERS.map((csvHeader) => csvHeader.label);

const handleExportCsv = (featureFlags: any[], dispatch: any) => async () => {
  try {
    downloadAsCsvFile({
      rows: [
        headerRow,
        ...featureFlags.map((ff) => [
          ff.feature,
          ff.notes,
          ff.isFractional,
          ff.settings.enabledGlobally || ff.settings.globalFraction,
          ff.settings.enabledOrgIds || Object.keys(ff.settings.orgIdFractions),
          ff.isFractional
            ? Object.keys(ff.settings.orgIdFractions).length
            : ff.settings.enabledOrgIds.length,
          ff.settings.enabledProjectIds || Object.keys(ff.settings.projectIdFractions),
          ff.isFractional
            ? Object.keys(ff.settings.projectIdFractions).length
            : ff.settings.enabledProjectIds.length,
          ff.settings.disabledOrgIds,
          ff.isFractional ? 'N/A' : ff.settings.disabledOrgIds.length,
          ff.settings.disabledProjectIds,
          ff.isFractional ? 'N/A' : ff.settings.disabledProjectIds.length,
        ]),
      ],
      fileName: `Feature flags.csv`,
    });
    dispatch(addPositiveToast(`Success! Exported feature flags.`));
  } catch (e) {
    dispatch(addNegativeToast(`Failed to export feature flags.`));
  }
};

export const CsvExportButton = ({ sortedFeatures }: CsvExportButtonProps) => {
  const dispatch = useDispatch();
  const exportToCsv = useMemo(
    () => handleExportCsv(sortedFeatures, dispatch),
    [sortedFeatures, dispatch],
  );

  return (
    <Tooltip
      tooltipContent={TOOLTIP_DISPLAY_TEXT.disabledTooltip}
      isEnabled={!sortedFeatures.length}
    >
      <StyledButton
        data-test="csv-export-button"
        onClick={exportToCsv}
        size={BUTTON_SIZES.LARGE}
        isDisabled={!sortedFeatures.length}
      >
        <Export24 />
        <span>Export feature flags</span>
      </StyledButton>
    </Tooltip>
  );
};
